import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';

export type GalleryPreviewItem = {
  id?: string | number;
  title?: string;
  description?: string;
  image?: string;
  image_url?: string;     // Added for API compatibility
  thumbnail_url?: string; // Added for API compatibility
};

type Props = {
  items?: GalleryPreviewItem[];
};

const fallbackGallery: GalleryPreviewItem[] = [
  {
    id: 'twilight-duo',
    title: 'Twilight Grooming Ritual',
    description: 'A bonded pair share an evening grooming session bathed in soft golden light.',
    image: '/images/adults2.jpeg',
  },
  {
    id: 'sunroom-serenade',
    title: 'Sunroom Serenade',
    description: 'Curious eyes and silky coats bask in the warmth of the afternoon sun.',
    image: '/images/1a.jpeg',
  },
  {
    id: 'velvet-nap',
    title: 'Velvet Afternoon Nap',
    description: 'Soft breathing and gentle paws resting on plush textures.',
    image: '/images/1b.jpeg',
  },
  {
    id: 'playful-prance',
    title: 'Playful Prance',
    description: 'Feather wands and airborne jumps capture the spirit of enrichment play.',
    image: 'https://images.unsplash.com/photo-1526336024174-e58f5cdd8e13?auto=format&fit=crop&w=900&q=80',
  },
  {
    id: 'regal-portrait',
    title: 'Regal Portrait',
    description: 'A poised gaze showcases the elegance of Balinese lineage.',
    image: 'https://images.unsplash.com/photo-1512404760209-dcb4c2ed9039?auto=format&fit=crop&w=900&q=80',
  },
  {
    id: 'nursery-dawn',
    title: 'Nursery Dawn',
    description: 'Morning stretches in the nursery alcove as a new day begins.',
    image: 'https://images.unsplash.com/photo-1494256997604-768d1f608cac?auto=format&fit=crop&w=900&q=80',
  },
];

// Helper function to ensure we have a valid image URL
const getImageUrl = (item: GalleryPreviewItem) => {
  if (item.image) return item.image;
  if (item.image_url) return item.image_url;
  if (item.thumbnail_url) return item.thumbnail_url;
  return '';
};

export default function GalleryPreview({ items = [] }: Props) {
  // Use API data if available, otherwise fallback to local images
  const data = items.length ? items : fallbackGallery;

  // Handle image loading errors
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.style.display = 'none';
    const fallbackDiv = document.createElement('div');
    fallbackDiv.className = 'w-full h-full bg-gray-100 flex items-center justify-center';
    fallbackDiv.innerHTML = '<span class="text-gray-400">Image not available</span>';
    target.parentNode?.replaceChild(fallbackDiv, target);
  };

  return (
    <section className="relative overflow-hidden bg-primary-950 py-24 text-white">
      <motion.div
        className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-soft-gold/10 via-transparent to-transparent"
        animate={{ opacity: [0.2, 0.45, 0.2] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="pointer-events-none absolute -bottom-40 left-1/4 h-80 w-80 rounded-full bg-amber-300/20 blur-3xl"
        animate={{ scale: [0.85, 1.1, 0.9], opacity: [0.18, 0.35, 0.18] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
      />
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col items-center text-center gap-4 mb-16">
          <motion.span
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2 text-xs uppercase tracking-[0.35em] text-soft-gold"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Gallery Prelude
          </motion.span>
          <motion.h2
            className="text-4xl md:text-5xl font-display font-semibold"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            An intimate glimpse into our Balinese world
          </motion.h2>
          <motion.p
            className="max-w-3xl text-lg text-primary-100"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Each vignette captures a quiet ritual, a playful leap, or the serene companionship that defines LINDY kittens.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.map((item, index) => {
            const imageUrl = getImageUrl(item);
            const hasValidImage = Boolean(imageUrl);

            return (
              <motion.div
                key={item.id || index}
                className="group relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-soft-gold/30 transition-all duration-300 hover:shadow-xl hover:shadow-soft-gold/10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '0px 0px -50px 0px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="aspect-square overflow-hidden bg-gray-900/30">
                  {hasValidImage ? (
                    <Image
                      src={imageUrl}
                      alt={item.title || 'Gallery image'}
                      width={600}
                      height={600}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      onError={handleImageError}
                      unoptimized={!imageUrl.includes('cloudinary.com')}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-800/30">
                      <span className="text-white/40">No image</span>
                    </div>
                  )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                  {item.title && (
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {item.title}
                    </h3>
                  )}
                  {item.description && (
                    <p className="text-white/80 text-sm">
                      {item.description}
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 flex justify-center">
          <Link
            href="/gallery"
            className="inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition-all hover:border-soft-gold hover:bg-soft-gold/80 hover:text-primary-900"
          >
            Explore the full gallery
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}
